import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ServiceDemoCTA from "./ServiceDemoCTA";

const services = [
  {
    id: "website-development",
    title: "Website Development",
    description:
      "CBS provides expert website development, creating, designing, and maintaining functional, high-performance digital spaces.",
    points: [
      "Static Websites",
      "Dynamic Websites",
      "CMS Solutions",
      "Responsive Web Apps",
    ],
    img: "/services/webdev.jpg",
  },
  {
    id: "application-development",
    title: "Application Development",
    description:
      "Custom software solutions built for web, mobile, desktop, and cross-platform environments. We create scalable, secure, and user-friendly applications tailored to business goals.",
    points: [
      "Web Application Development",
      "Mobile Apps (iOS & Android)",
      "Desktop Software Solutions",
      "Cross-platform App Development",
    ],
    img: "/services/appdev.jpg",
  },
  {
    id: "ecommerce-solutions",
    title: "E-commerce Solutions",
    description:
      "Scale your business with our robust B2B and B2C platforms designed for seamless user experiences and high conversion.",
    points: [
      "B2C Marketplaces",
      "B2B Trade Portals",
      "Secure Payment Integration",
      "Inventory Management",
    ],
    img: "/services/ecomservice.jpg",
  },
  {
    id: "ai-integration",
    title: "AI Integration Services",
    description:
      "Leverage pre-trained models and fine-tuning to embed intelligence into your business processes, from chatbots to predictive analytics.",
    points: [
      "Workflow Automation",
      "NLP & Chatbots",
      "Computer Vision",
      "Model Fine-Tuning",
    ],
    img: "/services/aiservices.jpg",
  },
  {
    id: "cloud-managed",
    title: "Cloud & Managed Services",
    description:
      "Complete management of cloud resources including migration, security, and optimization powered by AWS Certified Engineers.",
    points: [
      "Cloud Migration",
      "System Upgrades",
      "Data Center Virtualization",
      "24/7 Maintenance",
    ],
    img: "/services/cloud.jpg",
  },
];

export default function ServicePage() {
  return (
    <div className="min-h-screen bg-[#0B0F19] text-white font-sans pb-20 md:pb-32">
      <section className="relative min-h-[400px] md:h-[600px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/photos/hero/bg2.jpg"
            className="w-full h-full object-cover opacity-30 scale-105"
            alt="Background"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19] via-transparent to-[#0B0F19]"></div>
        </div>

        <div className="relative z-10 text-center px-4 md:px-6 max-w-5xl">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] md:w-[600px] md:h-[600px] bg-cyan-500/5 rounded-full blur-[80px] md:blur-[120px] pointer-events-none -z-10"></div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-black uppercase italic tracking-tighter leading-[0.9] md:leading-[0.85] mb-4 md:mb-6">
            Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">
              Services
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="max-w-2xl mx-auto text-gray-400 text-sm md:text-lg lg:text-xl font-medium leading-relaxed px-2">
            Comprehensive services to transform your business
          </motion.p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
        {/* Services Section */}
        <section className="mb-12 md:mb-20">
          <h2 className="text-2xl md:text-3xl font-black uppercase italic mb-6 md:mb-8 text-[#05DFAD]">
            Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative bg-[#161B22] border border-white/10 rounded-xl md:rounded-2xl overflow-hidden hover:border-[#05DFAD]/40 transition-all">
                <div className="aspect-video overflow-hidden">
                  <img
                    src={service.img}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-black text-white mb-2">
                    {service.title}
                  </h3>
                  <p className="text-xs md:text-sm text-slate-400 mb-4 line-clamp-2">
                    {service.description}
                  </p>
                  <Link
                    to={`/services/${service.id}`}
                    className="inline-flex items-center gap-2 text-[#05DFAD] font-semibold hover:text-white transition-colors text-sm md:text-base">
                    Learn More
                    <ArrowRight
                      size={16}
                      className="group-hover:translate-x-1 transition-transform"
                    />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>

      <ServiceDemoCTA />
    </div>
  );
}
